import { useCallback, useMemo, useRef, useState } from 'react'

import {
  IOperationRuleInput,
  OperationSubjectType,
  OperationType,
} from '~/client/graph'
import { TagType } from '~/client/src/shared/enums/TagType'
import { AndOrOperator } from '~/client/src/shared/models/LogicOperation'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import {
  DELETE_MANY_OPERATION_RULES,
  SAVE_MANY_OPERATION_RULES,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import {
  buildExpression,
  FilterCategory,
  getUsersFromAssignment,
  IFilterGroup,
  IRuleFilterState,
  parseExpression,
} from './signUpNotificationRuleUtils'

export interface ISignUpNotificationRulesApi {
  rules: OperationRule[]
  isDirty: boolean
  isSaving: boolean
  save: () => void
  createNewRule: () => OperationRule
  removeRule: (ruleId: string) => void
  duplicateRule: (ruleId: string) => void
  removeUserFromRule: (rule: OperationRule, userId: string) => void
  applyUserChanges: (rule: OperationRule, newUserIds: Set<string>) => void
  getUsersFromAssignment: (assignmentExpressions: string[]) => User[]

  // Generic filter CRUD
  getFilterState: (rule: OperationRule) => IRuleFilterState
  addFilter: (rule: OperationRule) => void
  duplicateFilter: (rule: OperationRule, filterIndex: number) => void
  removeFilter: (rule: OperationRule, filterIndex: number) => void
  setFilterCategory: (
    rule: OperationRule,
    filterIndex: number,
    category: FilterCategory,
  ) => void
  setFilterIsNot: (
    rule: OperationRule,
    filterIndex: number,
    isNot: boolean,
  ) => void
  setFilterValues: (
    rule: OperationRule,
    filterIndex: number,
    valueIds: string[],
  ) => void
  addFilterValue: (
    rule: OperationRule,
    filterIndex: number,
    valueId: string,
  ) => void
  removeFilterValue: (
    rule: OperationRule,
    filterIndex: number,
    valueId: string,
  ) => void
  setConnector: (
    rule: OperationRule,
    connectorIndex: number,
    connector: AndOrOperator,
  ) => void
}

function makeTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function isTempId(id: string): boolean {
  return id.startsWith('temp-')
}

export function useSignUpNotificationRules(params: {
  eventsStore: EventsStore
  operationRulesStore: OperationRulesStore
  projectMembersStore: ProjectMembersStore
}): ISignUpNotificationRulesApi {
  const { eventsStore, operationRulesStore, projectMembersStore } = params

  const [localNewRules, setLocalNewRules] = useState<OperationRule[]>([])
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set())
  const [filterOverrides, setFilterOverrides] = useState<
    Map<string, IRuleFilterState>
  >(new Map())
  const [renderTick, setRenderTick] = useState(0)

  const filterOverridesRef = useRef(filterOverrides)
  filterOverridesRef.current = filterOverrides

  const bumpRender = useCallback(() => setRenderTick(n => n + 1), [])

  const markDirty = useCallback(
    (id: string) => {
      if (!isTempId(id)) {
        setDirtyIds(prev => {
          const next = new Set(prev)
          next.add(id)
          return next
        })
      }
      bumpRender()
    },
    [bumpRender],
  )

  const storeRules = useMemo(
    () =>
      operationRulesStore.list
        .filter(
          rule =>
            rule.operationSubject === OperationSubjectType.SignUpNotification &&
            !deletedIds.has(rule.id),
        )
        .sort((a, b) => a.createdAt - b.createdAt),
    [operationRulesStore.list, deletedIds],
  )
  const rules = useMemo(
    () => [...storeRules, ...localNewRules],
    [storeRules, localNewRules],
  )

  const isDirty =
    dirtyIds.size > 0 || localNewRules.length > 0 || deletedIds.size > 0

  const isSaving =
    !!eventsStore.appState.loading.get(SAVE_MANY_OPERATION_RULES) ||
    !!eventsStore.appState.loading.get(DELETE_MANY_OPERATION_RULES)

  const lookupUser = useCallback(
    (id: string) => projectMembersStore.getById(id),
    [projectMembersStore],
  )

  const clearLocalState = useCallback(() => {
    setLocalNewRules([])
    setDeletedIds(new Set())
    setDirtyIds(new Set())
    setFilterOverrides(new Map())
  }, [])

  const save = useCallback(() => {
    const dirtyExisting = storeRules.filter(r => dirtyIds.has(r.id))
    const allToSave = [...dirtyExisting, ...localNewRules]

    const hasSaves = allToSave.length > 0
    const hasDeletes = deletedIds.size > 0

    if (!hasSaves && !hasDeletes) return

    // Track how many operations need to complete before clearing state
    let pending = (hasSaves ? 1 : 0) + (hasDeletes ? 1 : 0)
    const onOperationDone = () => {
      pending--
      if (pending === 0) clearLocalState()
    }

    if (hasSaves) {
      const dtos: IOperationRuleInput[] = allToSave.map(r => {
        const dto = OperationRule.toDto(r)
        const override = filterOverridesRef.current.get(r.id)
        if (override) {
          dto.expression = buildExpression(override)
        }
        if (isTempId(r.id)) {
          dto.id = null
        }
        return dto
      })
      eventsStore.dispatch(SAVE_MANY_OPERATION_RULES, dtos, onOperationDone)
    }

    if (hasDeletes) {
      operationRulesStore.removeMany([...deletedIds], onOperationDone)
    }
  }, [
    storeRules,
    dirtyIds,
    localNewRules,
    deletedIds,
    eventsStore,
    operationRulesStore,
    clearLocalState,
  ])

  return useMemo<ISignUpNotificationRulesApi>(() => {
    function resolveFilterState(rule: OperationRule): IRuleFilterState {
      return (
        filterOverridesRef.current.get(rule.id) ??
        parseExpression(rule.expression)
      )
    }

    function applyFilterUpdate(
      rule: OperationRule,
      newState: IRuleFilterState,
    ) {
      setFilterOverrides(prev => {
        const m = new Map(prev)
        m.set(rule.id, newState)
        return m
      })
      rule.expression = buildExpression(newState)
      markDirty(rule.id)
    }

    return {
      rules,
      isDirty,
      isSaving,
      save,

      removeUserFromRule(rule: OperationRule, userId: string) {
        if (!userId || !rule?.assignmentsAsSubexpressions?.length) {
          return
        }

        const tagExpression = OperationRule.makeTagExpressionString(
          TagType.User,
          userId,
        )
        const { assignmentsAsSubexpressions } = rule
        const expressionIdx = assignmentsAsSubexpressions.findIndex(
          v => v === tagExpression,
        )

        if (expressionIdx !== -1) {
          assignmentsAsSubexpressions.splice(expressionIdx, 1)
          if (!assignmentsAsSubexpressions.length) {
            assignmentsAsSubexpressions.push(
              OperationRule.ASSIGNMENT_PLACEHOLDER,
            )
          }
          rule.updateAssignmentExpressions()
          markDirty(rule.id)
        }
      },

      createNewRule(): OperationRule {
        const projectId = eventsStore.appState.activeProject?.id
        if (!projectId) {
          throw new Error('Cannot create rule without an active project')
        }

        const rule = new OperationRule(
          '',
          OperationType.Notification,
          OperationSubjectType.SignUpNotification,
          projectId,
          OperationRule.ASSIGNMENT_PLACEHOLDER,
          makeTempId(),
          Date.now(),
        )

        setLocalNewRules(prev => [...prev, rule])
        return rule
      },

      removeRule(ruleId: string) {
        if (isTempId(ruleId)) {
          setLocalNewRules(prev => prev.filter(r => r.id !== ruleId))
        } else {
          setDeletedIds(prev => {
            const next = new Set(prev)
            next.add(ruleId)
            return next
          })
        }
        setFilterOverrides(prev => {
          if (!prev.has(ruleId)) {
            return prev
          }
          const next = new Map(prev)
          next.delete(ruleId)
          return next
        })
      },

      duplicateRule(ruleId: string) {
        const source =
          localNewRules.find(r => r.id === ruleId) ||
          operationRulesStore.getRuleById(ruleId)
        if (!source) {
          return
        }

        const newId = makeTempId()
        const duplicate = new OperationRule(
          source.expression,
          OperationType.Notification,
          OperationSubjectType.SignUpNotification,
          source.projectId,
          source.assignExpression,
          newId,
          Date.now(),
        )

        const sourceFilter = resolveFilterState(source)
        setFilterOverrides(prev => {
          const m = new Map(prev)
          m.set(newId, {
            filters: sourceFilter.filters.map(f => ({
              ...f,
              valueIds: [...f.valueIds],
            })),
            connectors: [...sourceFilter.connectors],
          })
          return m
        })

        setLocalNewRules(prev => [...prev, duplicate])
      },

      applyUserChanges(rule: OperationRule, newUserIds: Set<string>) {
        const currentIds = new Set(
          getUsersFromAssignment(
            rule.assignmentsAsSubexpressions,
            lookupUser,
          ).map(u => u.id),
        )

        let changed = false

        for (const id of currentIds) {
          if (!newUserIds.has(id)) {
            const tagExpr = OperationRule.makeTagExpressionString(
              TagType.User,
              id,
            )
            const idx = rule.assignmentsAsSubexpressions.findIndex(
              v => v === tagExpr,
            )
            if (idx !== -1) {
              rule.assignmentsAsSubexpressions.splice(idx, 1)
              changed = true
            }
          }
        }

        for (const id of newUserIds) {
          if (!currentIds.has(id)) {
            const tagExpr = OperationRule.makeTagExpressionString(
              TagType.User,
              id,
            )
            if (!rule.assignmentsAsSubexpressions.includes(tagExpr)) {
              rule.assignmentsAsSubexpressions.push(tagExpr)
              changed = true
            }
          }
        }

        if (changed) {
          if (!rule.assignmentsAsSubexpressions.length) {
            rule.assignmentsAsSubexpressions.push(
              OperationRule.ASSIGNMENT_PLACEHOLDER,
            )
          }
          rule.updateAssignmentExpressions()
          markDirty(rule.id)
        }
      },

      getUsersFromAssignment(assignmentExpressions: string[]): User[] {
        return getUsersFromAssignment(assignmentExpressions, lookupUser)
      },

      // --- Generic filter CRUD ---

      getFilterState(rule: OperationRule): IRuleFilterState {
        return resolveFilterState(rule)
      },

      addFilter(rule: OperationRule) {
        const state = resolveFilterState(rule)
        const newFilter: IFilterGroup = {
          category: 'company',
          isNot: false,
          valueIds: [],
        }
        const newState: IRuleFilterState = {
          filters: [...state.filters, newFilter],
          connectors:
            state.filters.length > 0
              ? [...state.connectors, AndOrOperator.AND]
              : [],
        }
        applyFilterUpdate(rule, newState)
      },

      duplicateFilter(rule: OperationRule, filterIndex: number) {
        const state = resolveFilterState(rule)
        const source = state.filters[filterIndex]
        if (!source) return
        const duplicate: IFilterGroup = {
          category: source.category,
          isNot: source.isNot,
          valueIds: [...source.valueIds],
        }
        const newFilters = [...state.filters]
        newFilters.splice(filterIndex + 1, 0, duplicate)
        const newConnectors = [...state.connectors]
        newConnectors.splice(filterIndex, 0, AndOrOperator.AND)
        applyFilterUpdate(rule, {
          filters: newFilters,
          connectors: newConnectors,
        })
      },

      removeFilter(rule: OperationRule, filterIndex: number) {
        const state = resolveFilterState(rule)
        const newFilters = state.filters.filter((_, i) => i !== filterIndex)
        const newConnectors = [...state.connectors]
        if (newConnectors.length > 0) {
          const connectorToRemove = filterIndex > 0 ? filterIndex - 1 : 0
          newConnectors.splice(connectorToRemove, 1)
        }
        applyFilterUpdate(rule, {
          filters: newFilters,
          connectors: newConnectors,
        })
      },

      setFilterCategory(
        rule: OperationRule,
        filterIndex: number,
        category: FilterCategory,
      ) {
        const state = resolveFilterState(rule)
        const newFilters = state.filters.map((f, i) =>
          i === filterIndex ? { ...f, category, valueIds: [] } : f,
        )
        applyFilterUpdate(rule, { ...state, filters: newFilters })
      },

      setFilterIsNot(rule: OperationRule, filterIndex: number, isNot: boolean) {
        const state = resolveFilterState(rule)
        const newFilters = state.filters.map((f, i) =>
          i === filterIndex ? { ...f, isNot } : f,
        )
        applyFilterUpdate(rule, { ...state, filters: newFilters })
      },

      setFilterValues(
        rule: OperationRule,
        filterIndex: number,
        valueIds: string[],
      ) {
        const state = resolveFilterState(rule)
        const newFilters = state.filters.map((f, i) =>
          i === filterIndex ? { ...f, valueIds } : f,
        )
        applyFilterUpdate(rule, { ...state, filters: newFilters })
      },

      addFilterValue(
        rule: OperationRule,
        filterIndex: number,
        valueId: string,
      ) {
        const state = resolveFilterState(rule)
        const filter = state.filters[filterIndex]
        if (!filter || filter.valueIds.includes(valueId)) return
        const newFilters = state.filters.map((f, i) =>
          i === filterIndex ? { ...f, valueIds: [...f.valueIds, valueId] } : f,
        )
        applyFilterUpdate(rule, { ...state, filters: newFilters })
      },

      removeFilterValue(
        rule: OperationRule,
        filterIndex: number,
        valueId: string,
      ) {
        const state = resolveFilterState(rule)
        const newFilters = state.filters.map((f, i) =>
          i === filterIndex
            ? { ...f, valueIds: f.valueIds.filter(id => id !== valueId) }
            : f,
        )
        applyFilterUpdate(rule, { ...state, filters: newFilters })
      },

      setConnector(
        rule: OperationRule,
        connectorIndex: number,
        connector: AndOrOperator,
      ) {
        const state = resolveFilterState(rule)
        const newConnectors = [...state.connectors]
        newConnectors[connectorIndex] = connector
        applyFilterUpdate(rule, { ...state, connectors: newConnectors })
      },
    }
  }, [
    rules,
    isDirty,
    isSaving,
    save,
    lookupUser,
    markDirty,
    localNewRules,
    operationRulesStore,
    eventsStore,
    filterOverrides,
    renderTick,
  ])
}
